import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { estimateEditorVideo } from '../api/videoEditor.api';
import { buildEstimatePayload } from '../utils/estimateFormat';

/**
 * Manual simulation request (no automatic calls). `generate()` asks the backend to dry-run the real render
 * pipeline with the exact payload the creation would send. The previous result stays available, flagged `stale`,
 * when any relevant input changes afterwards.
 */
const useEditorEstimate = (config, feedId) => {
  const [estimate, setEstimate] = useState(null);
  const [estimateKey, setEstimateKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  const { period, customStart, customEnd, intervalMinutes, durationSeconds } = config;

  const payload = useMemo(
    () => buildEstimatePayload({ period, customStart, customEnd, intervalMinutes, durationSeconds }, feedId),
    [period, customStart, customEnd, intervalMinutes, durationSeconds, feedId]
  );
  const payloadKey = payload ? JSON.stringify(payload) : null;

  const cancel = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setLoading(false);
  }, []);

  // Inputs changed while a request is in flight: drop it, its result would already be outdated.
  useEffect(() => {
    cancel();
  }, [payloadKey, cancel]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const generate = useCallback(async () => {
    if (!payloadKey) return;
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await estimateEditorVideo(JSON.parse(payloadKey), { signal: controller.signal });
      if (controller.signal.aborted) return;
      setEstimate(data);
      setEstimateKey(payloadKey);
      setLoading(false);
      controllerRef.current = null;
    } catch (err) {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      setError('No se pudo generar la simulación. Intentá nuevamente.');
      setLoading(false);
      controllerRef.current = null;
    }
  }, [payloadKey]);

  return {
    estimate,
    loading,
    error,
    stale: estimate !== null && estimateKey !== payloadKey,
    canGenerate: payloadKey !== null,
    generate,
    cancel,
  };
};

export default useEditorEstimate;
